import * as React from "react";

import { cn } from "@/lib/cn";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  ariaDescription?: string;
  hasError?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, ariaDescription, hasError, ...props }, ref) => {
    const descriptionId = React.useId();

    // Fallback: if caller did not provide any accessible name (aria-label,
    // aria-labelledby, or an associated <label htmlFor>), use the placeholder
    // as aria-label so the input is at least reachable to screen readers.
    const hasExplicitAccessibleName = Boolean(
      props["aria-label"] || props["aria-labelledby"] || props.id,
    );
    const fallbackAriaLabel =
      !hasExplicitAccessibleName && typeof props.placeholder === "string"
        ? props.placeholder
        : undefined;

    const accessibilityProps = {
      ...props,
      "aria-label": props["aria-label"] ?? fallbackAriaLabel,
      "aria-invalid": hasError || props["aria-invalid"],
      "aria-describedby": ariaDescription
        ? `${descriptionId} ${props["aria-describedby"] || ""}`.trim()
        : props["aria-describedby"],
      tabIndex: props.tabIndex ?? 0,
    };

    return (
      <>
        <input
          type={type}
          className={cn(
            "shadow-xs flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 ",
            hasError && "border-destructive focus-visible:outline-destructive",
            className,
          )}
          ref={ref}
          {...accessibilityProps}
        />
        {ariaDescription && (
          <div id={descriptionId} className="sr-only">
            {ariaDescription}
          </div>
        )}
      </>
    );
  },
);

Input.displayName = "Input";

export { Input };
